import React, { useRef, useState } from "react";
import styles from "./styles.module.scss";
import SearchCasesNew from "components/SearchCasesNew";
import Input from "components/Input";
import SearchByNip from "components/SearchByNip";
import { IData } from ".";
import { IEntity } from "dto/IEntity";
import { useOnClickOutside } from "hooks/useClickOutside";
import { useTranslation } from "react-i18next";
import { isValidPesel } from "utils/isValidPesel";

/**
 * Atrapa długości PESEL-u wyświetlana jak hasło — nigdy nie trafia do danych formularza. Kropki jako zwykły tekst,
 * nie pole type="password": menedżery haseł proponowałyby zapis „hasła” razem z danymi osoby.
 */
const MASKED_PESEL = "•".repeat(11);

interface PersonFormProps {
  data: IData;
  setData: (data: IData) => void;
  handleAdd: (type: string, regon?: string, address?: any) => void;
  fetchData: (index: number) => void;
  isPackages: boolean;
  entities: IEntity[];
}

const PersonForm: React.FC<PersonFormProps> = ({
  data,
  setData,
  handleAdd,
  fetchData,
  isPackages,
  entities,
}) => {
  const { t } = useTranslation();
  const handleCompanyData = (company: any) => {
    handleAdd("all", company.Regon, {
      city: company.Place,
      street: company.Street,
      zipCode: company.ZipCode,
    });
  };

  const [showPersons, setShowPersons] = useState(false);
  const [editingPesel, setEditingPesel] = useState(false);

  const pesel = data.pesel ?? "";
  const savedPesel = !!data.hasSavedPesel && !data.peselRemoved;
  const peselError =
    pesel.length === 11 && !isValidPesel(pesel) ? t("client.peselInvalid") : null;

  // Zapisanego PESEL-u serwer nie zwraca (jest zaszyfrowany kluczem firmy), więc pole pokazuje przysłoniętą
  // atrapę jak pole hasła. Atrapa żyje tylko w widoku — w danych formularza PESEL zostaje pusty (= bez zmian).
  const showPeselMask = savedPesel && !pesel && !editingPesel;

  const dropdownRef = useRef(null);

  useOnClickOutside(dropdownRef, () => {
    setShowPersons(false);
  });

  return (
    <div className={styles.form}>
      <SearchByNip
        onDataChange={handleCompanyData}
        defaultValue={data.personNip ?? ""}
        onChange={(value) => setData({ ...data, personNip: value })}
      />

      <div className={styles.peselField}>
        <Input
          autoComplete="off"
          value={showPeselMask ? MASKED_PESEL : pesel}
          onFocus={() => setEditingPesel(true)}
          onBlur={() => setEditingPesel(false)}
          hideClearButton={showPeselMask}
          onChange={(value) => {
            if (showPeselMask) return;
            setData({ ...data, pesel: value.replace(/\D/g, "").slice(0, 11) });
          }}
          label={t("client.pesel")}
          inputProps={{ inputMode: "numeric", maxLength: 11 }}
          onRightText={savedPesel && !pesel ? t("common.delete") : undefined}
          onRightClick={
            savedPesel && !pesel
              ? () => setData({ ...data, peselRemoved: true })
              : undefined
          }
        />
        {peselError && <div className={styles.peselError}>{peselError}</div>}
        {!peselError && savedPesel && (
          <div className={styles.peselHint}>
            {t("client.peselSaved")}
          </div>
        )}
        {!peselError && data.peselRemoved && !pesel && (
          <div className={styles.peselHint}>{t("client.peselRemoved")}</div>
        )}
      </div>

      <div ref={dropdownRef} className={styles.personWrapper}>
        <Input
          value={data.name}
          onRightText={t("common.fetchData")}
          onRightClick={
            isPackages ? () => setShowPersons(!showPersons) : undefined
          }
          onChange={(value) => setData({ ...data, name: value })}
          label={t("forms.fullName")}
        />
        {showPersons && (
          <div className={styles.dropdown}>
            <div className={styles.dropdownWrapper}>
              {entities.map((entity, index) => {
                return (
                  <div
                    key={index}
                    onClick={() => {
                      fetchData(index);
                      setShowPersons(false);
                    }}
                    className={styles.dropdownBox}
                  >
                    {entity.Name.length ? entity.Name : entity.Email}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {data?.emails?.length > 0 && (
        <div className={styles.input}>
          <Input
            value={data.emails[0]}
            onChange={(value) => {
              const newEmails = [...data.emails];
              newEmails[0] = value;
              setData({ ...data, emails: newEmails });
            }}
            isEmail
            label={t("auth.email")}
          />
        </div>
      )}

      <div className={styles.input}>
        <Input
          isPhoneNumber
          value={data.phones[0]}
          onChange={(value) => {
            const newPhones = [...data.phones];
            newPhones[0] = value;
            setData({ ...data, phones: newPhones });
          }}
          label={t("forms.phoneNumber")}
        />
      </div>

      {data?.EAddresses?.length > 0 && (
        <div className={styles.input}>
          <Input
            value={data.EAddresses[0]}
            onChange={(value) => {
              const newAddresses = [...data.EAddresses];
              newAddresses[0] = value;
              setData({ ...data, EAddresses: newAddresses });
            }}
            label={t("forms.eDeliveryAddress")}
          />
        </div>
      )}
      <SearchCasesNew
        settedCase={data.Case}
        setAddNewEntity={() => {}}
        onChoose={(cas) => setData({ ...data, Case: cas })}
      />
    </div>
  );
};

export default PersonForm;
